import { useMediaQuery } from '@hover/blueprint';
import { useCallback } from 'react';

import { hasNativeShare } from 'src/shared/helpers/environment';
import { logError } from 'src/shared/telemetry/logger';

interface UseImageNativeShareParams {
  imageUrl: string;
  title?: string;
  text?: string;
  onShareFail?: () => void;
}
export const useNativeShareHandler = ({
  imageUrl,
  title,
  text,
  onShareFail,
}: UseImageNativeShareParams) =>
  useCallback(() => {
    const shareData = {
      title,
      text,
      url: imageUrl,
    };

    if (hasNativeShare() && navigator.canShare(shareData)) {
      void (async () => {
        try {
          await navigator.share(shareData);
        } catch (err) {
          const isAbortError =
            err instanceof DOMException && err.name === 'AbortError';
          if (!isAbortError) {
            logError(err as Error);
          }
          if (onShareFail) {
            onShareFail();
          }
        }
      })();
    }
  }, [text, title, imageUrl, onShareFail]);

// Desktop browsers have started to support native share, but for most use cases
// on desktop we want to use our own custom share logic.
export const useHasValidNativeShare = () => {
  const [isTouchDevice] = useMediaQuery('(hover: none)');

  return hasNativeShare() && isTouchDevice;
};
